import { EXCLUDE_ABSOLUTE_PATH_GENERATION_FOR_SITE_SELECTOR_LINKS } from "@/lib/siteconfig/constants"
import { type HreflangMap, hreflangKeyForLocale } from "@/src/utils/hreflangs/types"
import { isHrefLangKey } from "@/src/utils/strings/isHrefLangKey"

/** Only used to parse relative hrefs with the URL API; never appears in output. */
const PARSE_BASE = "https://relative.invalid"

/**
 * The part of the current path after the locale segment, reused when a locale has no
 * hreflang alternate. Checkout-like pages return `null` so the picker links to the
 * locale root instead of a page that cannot be shared across markets.
 */
export const extractResolvedPathAfterLocale = (pathname: string): string | null => {
    const segments = new URL(pathname, PARSE_BASE).pathname.split("/").filter(Boolean)

    if (segments.length < 2) return null

    const [, firstSegment, ...rest] = segments
    if (EXCLUDE_ABSOLUTE_PATH_GENERATION_FOR_SITE_SELECTOR_LINKS.includes(firstSegment)) {
        return null
    }

    return [firstSegment, ...rest].join("/")
}

/**
 * The link the picker shows for a locale: the page's hreflang alternate when there is
 * one (an absolute URL from the `<link rel="alternate">` tags), otherwise the current
 * path under that locale.
 */
export const buildLocaleHref = (
    localeSegment: string,
    hrefLangs: HreflangMap | undefined,
    pathAfterLocale: string | null
): string => {
    // The page publishes some locales under another key (`ja-jp` as `ja`).
    const key = hreflangKeyForLocale(localeSegment)
    const alternate = (isHrefLangKey(key) ? hrefLangs?.[key] : undefined) || ""
    const fallback = pathAfterLocale
        ? `/${localeSegment}/${pathAfterLocale}`
        : `/${localeSegment}`

    return alternate || fallback
}

/** The pathname of an absolute or relative href, which is what the resolve route takes. */
export const hrefPathname = (href: string): string => new URL(href, PARSE_BASE).pathname

/**
 * Swaps the pathname of `href` for its resolved counterpart, keeping the href's form:
 * an absolute URL stays absolute with its origin, a relative path stays relative.
 * Vercel rules may also send visitors off-site or attach a query string; an absolute
 * destination is followed as-is, a path with query or hash is grafted on with them.
 */
export const applyResolvedPathname = (
    href: string,
    resolved: Record<string, string> | undefined
): string => {
    if (!resolved) return href

    const url = new URL(href, PARSE_BASE)
    const target = resolved[url.pathname]
    if (!target || target === url.pathname) return href

    if (/^https?:\/\//i.test(target)) return target
    if (!target.startsWith("/") || target.startsWith("//")) return href

    const destination = new URL(target, url.origin)
    url.pathname = destination.pathname
    if (destination.search) url.search = destination.search
    if (destination.hash) url.hash = destination.hash

    return url.origin === PARSE_BASE
        ? `${url.pathname}${url.search}${url.hash}`
        : url.toString()
}
